<script>
import { mapActions, mapState } from 'vuex';
import i18n from "@/plugins/i18n";

import Stakeholders from './components/stakeholders.vue';

export default {
    components: {
        Stakeholders
    },

    data() {
        return {
            loading: false,
            incompleteStep: null,

            sectors: [],

            selectedTab: "active",

            keyword: null,
            sortBy: null,
            increasing: true,
            currentPage: 1,

            companies: [],
            loadMoreEnabled: false,

            companyToUpdate: null,

            activeCompany: null
        }
    },

    computed: {
        isAdmin() {
            const info = this.$user;
            return info.role_id == 1;
        },
        
        ...mapState('app', ['companyLicence']),

        ...mapState('settings', [
			'process_track_id',
			'steps'
		]),

        isAuditor() {
            if (!this.companyLicence.plan_id) return false;
            return this.companyLicence.plan_id === -1;
        },

        isLoadingMore() {
            return this.loading && this.companies.length > 0;
        },

        isUnauthorized() {
            return this.incompleteStep != null;
        },

        internalsWarningEnabled() {
            if (!this.steps[1]) return false;
            return this.steps[1].completed == false;
        },
        
        externalsWarningEnabled() {
            if (!this.steps[4]) return false;
            return this.steps[4].completed == false;
        },

        showStakeholderStepWarnings() {
            if (this.isAdmin) {
                return false;
            } else {
                return this.internalsWarningEnabled || this.externalsWarningEnabled;
            }
        },

        tableHeaders() {
            return [
                {text: "#", width: "4.5%", key: "row"},
                {text: i18n.t("userCompany.company.name"), width: "17%", key: "company_name"},
                {text: i18n.t("userCompany.company.user_number"), width: "7.5%", key: "user_number"},
                {text: i18n.t("userCompany.company.sector"), width: "25%", key: "sector_name"},
                {text: i18n.t("userCompany.company.website"), width: "20%", key: "website"},
                {text: i18n.t("userCompany.company.address"), width: "20%", key: "adress"},
                {text: i18n.t("userCompany.company.edit"), no_click:true , width: "5%", key: "edit"},
                {text: i18n.t("userCompany.company.delete"), no_click:true  , width: "5%", key: "delete"}
            ]
        },

        actualHeaders() {
            if (this.isAdmin) {
                return this.tableHeaders;
            } else {
                return [
                    {text: "#", width: "4.5%", key: "row"},
                    {text: i18n.t("userCompany.company.name"), width: "17%", key: "company_name"},
                    {text: i18n.t("userCompany.company.user_number"), width: "7.5%", key: "user_number"},
                    {text: i18n.t("userCompany.company.sector"), width: "25%", key: "sector_name"},
                    {text: i18n.t("userCompany.company.website"), width: "25%", key: "website"},
                    {text: i18n.t("userCompany.company.address"), width: "25%", key: "adress"},
                ]
            }
        }
    },

    async mounted() {
        await this._getSectors();

        if (!this.isAuditor) {
            await this.handleProcessTrack();
        }
        
        await this._getCompanyOfUser();
    },

    methods: {
        ...mapActions("settings", [
            "getCompanyOfUser",
            "getProcessTrack",
            "updateProcessTrack"
        ]),

        ...mapActions("company", [
            "getSector",
            "updateCompany",
            "deleteCompany"
        ]),

        async handleProcessTrack() {
            const step = this.steps.find(item => (item.completed == false && item.message != undefined && item.step_number < 2));
            if (step) {
                await this.setIncompleteStep(step);
                setTimeout(() => {
                    this.$refs.stepModal.openPopup();
                }, 150);
            }
        },

        async _updateProcessTrack(stakeholder_type) {
            if ((stakeholder_type == "internal" || stakeholder_type == "external") == false) {
                console.error("Invalid stakeholder type input!");
                return;
            }

            const params = {
                updated_by: this.$user.user_id,
                [`is_step_${stakeholder_type == 'internal' ? '2' : '5'}_completed`]: true,
                reporting_process_track_id: this.process_track_id
            }
            this.updateProcessTrack(params)
                .then( res => {
                    this.steps[stakeholder_type === 'internal' ? 1 : 4].completed = true;
                    this.$notify("success", res.data.messageName);
                })
                .catch(({ response }) => {
                    console.error("Error updating process tracks: \n", response.data.messageName);
                })
        },

        async _getSectors() {
            const params = { 
                is_deleted: false
            }

            this.getSector(params)
                .then( res => {
                    const lang = localStorage.getItem("lang");

                    this.sectors = res.data.map(item => {
                        return {
                            text: lang == "tr" && item.sust_sector_name_tr ? item.sust_sector_name_tr : item.sust_sector_name,
                            value: item.sust_sector_id
                        }
                    })
                })
                .catch(error => {
                    throw error;
                })
        },

        async _getCompanyOfUser() {
            await this.setLoading(true);

            const params = {
                is_deleted: this.selectedTab == "deleted",
                keyword: this.keyword,
                sort_by: this.sortBy == "row" ? null : this.sortBy,
                increasing: this.increasing,
                page: this.currentPage
            }

            await this.getCompanyOfUser(params)
                .then( async (res) => {
                    await this.setCompanies(res.data.companies);
                    await this.setLoadMoreEnabled(res.data.load_more);

                    if (res.data.load_more == false) {
                        await this.changeCurrentPage(-1);
                    }
                })
                .catch(({ response }) => {
                    this.$notify("error", response.data.messageName);
                })
                .finally(async () => {
                    if (this.companies.length > 0) {
                        const users_own_company_idx = this.companies.findIndex(x => x.company_id === this.$user.company_id);
                        this.setActiveCompany(this.companies[users_own_company_idx === -1 ? 0 : users_own_company_idx]);
                    }

                    await this.setLoading(false);
                })
        },

        async _updateCompany() {
            if (this.isUnauthorized) return;

            const params = {
                company_id: this.companyToUpdate.company_id,
                body: {
                    company_name: this.companyToUpdate.company_name,
                    company_fullname: this.companyToUpdate.company_fullname,
                    website: this.companyToUpdate.website,
                    sector_id: this.companyToUpdate.sector_id,
                    adress: this.companyToUpdate.adress,
                    updated_by: this.$user.user_id
                }
            }

            await this.updateCompany(params)
                .then( res => {
                    const company = this.companies.find( item => item.company_id == params.company_id);
                    if (company) {
                        for (const key in params.body) {
                            if (Object.hasOwnProperty.call(params.body, key)) {
                                try {
                                    const element = params.body[key];
                                    if (company[key]) {
                                        company[key] = element;
                                    }
                                } catch (error) {
                                    console.warn("Failed Key: ", key);
                                    console.error(error.message);
                                }
                            }
                        }
                    } else {
                        console.error("No company was found in the already established companies.");
                        console.error("Companies: ", this.companies);
                        console.error("Id that couldn't be found: ", params.company_id);
                        throw null;
                    }

                    this.$notify("success", res.data.messageName);
                })
                .catch(({ response }) => {
                    this.$notify("error", response.data.messageName);
                })
        },

        async _deleteCompany() {
            if (this.isUnauthorized) return;

            const params = {
                company_id: this.companyToUpdate.company_id,
                user_id: this.$user.user_id
            }

            await this.deleteCompany(params)
                .then( res => {
                    const index = this.companies.findIndex(item => item.company_id == params.company_id);
                    if (index > -1) {
                        this.companies.splice(index, 1);
                    }
                    this.$notify("success", res.data.messageName);
                })
                .catch(({ response }) => {
                    this.$notify("error", response.data.messageName);
                })
        },

        // event handlers //

        handleStepWarningOk() {
            if (this.incompleteStep) {
                this.$router.push(this.incompleteStep.route_link);
            } else {
                console.error("No incomplete step found! This is unexpected behaviour.");
            }
        },

        async handleSelectedTab(tab) {
            if (this.isUnauthorized) return;

            await this.setSelectedTab(tab);
            await this.handleReset();
        },

        async handleLoadMore() {
            await this.setLoading(true);
            setTimeout(async () => {
                await this.changeCurrentPage(1);
                await this._getCompanyOfUser();
            }, 150);
        },

        async handleSortBy(header) {
            if(header.no_click) return;
            if (this.isUnauthorized) return;

            if (this.sortBy != header.key) {
                await this.setSortBy(header.key);
                await this.setIncreasing(false);
            } else {
                await this.setIncreasing(!this.increasing);
            }
            await this._getCompanyOfUser();
        },

        async handleReset() {
            if (this.isUnauthorized) return;

            await this.resetKeyword();
            await this.setSortBy(null);
            await this.setIncreasing(false);
            await this._getCompanyOfUser();
        },

        handleClickEdit(company) {
            if (this.isUnauthorized) return;

            this.companyToUpdate = {...company}; // spread operator used so that companyToUpdate is not bound to the actual company object
            this.$refs.editPopup.openPopup();
        },

        handleClickDelete(company) {
            if (this.isUnauthorized) return;

            this.companyToUpdate = {...company};
            this.$refs.deletePopup.openPopup();
        },

        // setters //

        async setIncompleteStep(step) {
            this.incompleteStep = step;
        },

        async setCompanies(_companies) {
            this.companies = _companies;
        },

        async setLoadMoreEnabled(enabled) {
            this.loadMoreEnabled = enabled;
        },

        async setLoading(_loading) {
            this.loading = _loading;
        },

        async setSelectedTab(tab) {
            this.selectedTab = tab;
        },

        async changeCurrentPage(step) {
            if (this.currentPage + step > 0) {
                this.currentPage += step;
            }
        },

        async setSortBy(key) {
            this.sortBy = key;
        },

        async setIncreasing(key) {
            this.increasing = key;
        },

        async resetKeyword() {
            this.keyword = null;
        },

        resetUpdateCompany() {
            this.companyToUpdate = null;
        },

        setActiveCompany(company) {
            if (this.isUnauthorized) return;

            if (this.activeCompany != company) {
                this.activeCompany = company
            } else {
                this.activeCompany = null;
            }
        },
        onUserRegister(company, stakeHolderType){
            company.user_number++;
            if(stakeHolderType=="internal"){
                company.internal_user_number++;
            }else{
                company.external_user_number++;
            }
        }
    }
}

</script>

<template>
    <div class="stakeholders-page" ref="stakeholdersPage" :class="{'unauthorized': isUnauthorized}">
        <div class="top-tabs" v-if="isAdmin">
            <div @click="handleSelectedTab('active')" :class="{'selected-tab': selectedTab == 'active'}">
                {{ $t('userCompany.company.active') }}
            </div>
            <div @click="handleSelectedTab('deleted')" :class="{'selected-tab': selectedTab == 'deleted'}">
                {{ $t('userCompany.company.deleted') }}
            </div>
        </div>

        <div class="stk-confirm-field" v-if="showStakeholderStepWarnings">
            <div class="stk-confirm-text">
                <i class="fa-solid fa-circle-exclamation" style="color: white;"></i>
                {{ $t('userCompany.company.stakeholder_warning') }}
            </div>

            <div class="stk-confirm-btns">
                <button v-if="internalsWarningEnabled" class="stk-confirm-button" @click="_updateProcessTrack('internal')">
                    {{ $t('userCompany.company.confirm_internals') }}
                </button>
                <button v-if="externalsWarningEnabled" class="stk-confirm-button" @click="_updateProcessTrack('external')">
                    {{ $t('userCompany.company.confirm_externals') }}
                </button>
            </div>
        </div>

        <div class="searchbar" v-if="isAdmin">
            <input type="text" v-model="keyword" :placeholder="$t('userCompany.company.search_placeholder')" @keydown.enter="_getCompanyOfUser()">
            <button class="search-btn" @click="_getCompanyOfUser()">
                <i class="fa-solid fa-magnifying-glass"></i>
            </button>

            <button class="reset-btn" @click="handleReset()" @keydown.enter="handleReset()">
                <i class="fa-solid fa-rotate-left"></i>
            </button>
        </div>

        <div class="companies-table">
            <div class="headers-row">
                <div class="flex-all-centered-row" v-for="header in actualHeaders" :key="header.key" :style="{width: header.width}"
                    :class="{'no-sorting': header.key == 'delete' || header.key == 'edit', 'sorting-col': sortBy == header.key , 'pointer-events': header.no_click ? 'none' : null}"
                    @click="handleSortBy(header)"
                >
                    {{ header.text }}
                <i v-if="sortBy == header.key " :class="`fa-solid fa-angle-${increasing ? 'down' : 'up'}`" ></i>
                </div>
            </div>

            <div class="values-row" v-for="(company, index) in companies" :key="company.company_id">
                <div class="cells" @click="setActiveCompany(company)">
                    <div class="value-cell" v-for="cell in actualHeaders" :key="cell.key" :style="{width: cell.width}">
                        <span v-if="cell.key != 'edit' && cell.key != 'delete' && cell.key != 'row'">
                            <span class="no-users-icon" v-if="cell.key == 'user_number'" :key="company.user_number">
                                <i v-if="company.user_number == 0" class="fa-solid fa-circle-exclamation"
                                    v-sust-tooltip="{text: $t('userCompany.company.no_zero_users'), bgColor: 'var(--icon-yellow)'}">
                                </i>
                                <i v-else class="fa-solid fa-circle-info" style="color: var(--sustainfinity-navy);"
                                    v-sust-tooltip="{
                                        text: `${$t('userCompany.company.internal')}: ${company.internal_user_number} | ${$t('userCompany.company.external')}: ${company.external_user_number}`
                                    }"
                                >  
                                </i>
                            </span>
                            <span v-if="cell.key == 'company_name'">
                                <i class="fa-solid fa-circle-info" 
                                    v-sust-tooltip="{
                                        text: company.company_fullname ? company.company_fullname : $t('userCompany.company.no_full_name'),
                                        bgColor: company.company_fullname ? 'var(--sustainfinity-navy)' : 'var(--icon-yellow)'
                                    }"
                                    :style="{color: company.company_fullname ? 'var(--sustainfinity-navy)' : 'var(--invalid-input)'}"
                                >
                                </i>
                            </span>

                            <span v-if="cell.key != 'sector_name'">
                                {{ company[cell.key] }}
                            </span>
                            <span v-else>
                                {{ sectors.find(x => x.value == company.sector_id).text }}
                            </span>
                        </span>
                        <div class="flex-all-centered-row" v-else>
                            <i tabindex="0" v-if="cell.key == 'edit'" class="fa-regular fa-pen-to-square edit-btn"
                                @click.stop="handleClickEdit(company)" @keydown.enter="handleClickEdit(company)"
                            >
                            </i>
                            <i tabindex="0" v-if="cell.key == 'delete'" class="fa-regular fa-trash-can delete-btn"
                                @click.stop="handleClickDelete(company)" @keydown.enter="handleClickDelete(company)"
                            >
                            </i>
                            <span v-if="cell.key == 'row'">{{ index + 1 }}</span>
                        </div>
                    </div>
                </div>

                <div class="stakeholders-ctr" v-if="activeCompany && activeCompany == company">
                    <stakeholders 
                        :companyId="company.company_id" :isAdmin="isAdmin" 
                        :userInfo="$user" :onDeletedTab="selectedTab == 'deleted'"
                        @userRegister = "($event) => onUserRegister(company,$event)"
                    />
                </div>
            </div>

            <div tabindex="0" class="load-more-row" v-if="loadMoreEnabled" @click="handleLoadMore()" @keydown.enter="handleLoadMore()">
                <span>
                    {{ $t('userCompany.company.load_more') }}
                    <i v-if="!isLoadingMore" class="fa-solid fa-angle-down"></i>
                    <i v-else class="fa-solid fa-spinner"></i>
                </span>
            </div>
        </div>

        <sust-popup
            ref="editPopup"
            size="lg"
            :title="$t('userCompany.company.update_title')"
            @ok="_updateCompany()"
            @onHide="resetUpdateCompany()"  
        >
            <div class="edit-form" v-if="companyToUpdate">
                <div class="small-field">
                    <label for="editField">
                        {{ $t('userCompany.company.name') }}
                    </label>
                    <input v-model="companyToUpdate.company_name" name="editField" type="text">
                </div>
                <div class="small-field">
                    <label for="editField">
                        {{ $t('userCompany.company.fullname') }}
                    </label>
                    <input v-model="companyToUpdate.company_fullname" name="editField" type="text">
                </div>
                <div class="small-field">
                    <label for="editField">
                        {{ $t('userCompany.company.website') }}
                    </label>
                    <input v-model="companyToUpdate.website" name="editField" type="text">
                </div>
                <div class="small-field">
                    <label for="editField">
                        {{ $t('userCompany.company.sector') }}
                    </label>
                    <select name="editField" v-model="companyToUpdate.sector_id">
                        <option v-for="option in sectors" :value="option.value" :key="option.value">
                            {{ option.text }}
                        </option>
                    </select>
                </div>
                <div class="small-field">
                    <label for="editField">
                        {{ $t('userCompany.company.address') }}
                    </label>
                    <textarea v-model="companyToUpdate.adress" name="editField" type="text"></textarea>
                </div>
            </div>
        </sust-popup>

        <sust-popup
            ref="deletePopup"
            size="sm"
            :title="$t('userCompany.company.delete_title')"
            @ok="_deleteCompany()"
            @onHide="resetUpdateCompany()"
        >
            <div>
                {{ $t('userCompany.company.delete_text') }}
            </div>
        </sust-popup>

        <sust-popup 
            v-if="incompleteStep"
            ref="stepModal"
            size="md"
            :title="$t('stepValidations.title')"
            @ok="handleStepWarningOk()"
        >
            {{ incompleteStep.message }} <br /> {{$t('stepValidations.confirmRedirect')}}
        </sust-popup>
    </div>
</template>

<style lang='scss' scoped>
    .stakeholders-page {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: center;

        width: 100%;

        > div {
            margin-top: 1em;

            &:last-of-type {
                margin-bottom: 1em;
            }
        }
    }

    .stakeholders-page.unauthorized {
        > * {
            pointer-events: none !important;
        }
    }

    .top-tabs {
        width: 100%;
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;

        border: thin solid var(--sustainfinity-navy);
        border-radius: 100px;

        overflow: hidden;
    
        > div {
            width: 100%;
            text-align: center;
            background-color: whitesmoke;
            color: var(--sustainfinity-navy);

            transition: all 150ms;
            cursor: pointer;

            &:hover {
                background-color: rgb(224, 224, 224);
            }
        }

        > .selected-tab {
            background-color: var(--sustainfinity-navy) !important;
            color: whitesmoke !important;
            pointer-events: none !important;
        }
    }

    .stk-confirm-field {
        align-self: flex-start;
        margin-top: 1em;
        width: 60%;
        background-color: var(--sustainfinity-blue);
        color: whitesmoke;
        text-align: center;
        padding: .5em;
        border-radius: .5em;

        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        > .stk-confirm-text {
            display: flex;
            width: 100%;
            text-align: left;
            font-weight: bold;

            > i {
                color: whitesmoke;
                padding-right: .5em;
                font-size: 1.5em;
            }
        }

        > .stk-confirm-btns {
            width: 100%;
            display: flex;
            flex-direction: row;
            justify-content: flex-end;

            > button {
                border: 0;
                border-radius: .5em;
                outline: thin solid whitesmoke;
                padding: .3em .6em .3em .6em;
                background-color: var(--sustainfinity-blue);
                filter: hue-rotate(5deg);
                color: whitesmoke;

                transition: all 150ms;

                &:nth-of-type(2) {
                    margin-left: .5em;
                }

                &:hover {
                    background-color: var(--sustainfinity-blue--hover);
                }

                &:active {
                    scale: .95;
                }

                &:focus {
                    outline: thin solid var(--sustainfinity-navy) !important;
                }
            }
        } 
    }

    .searchbar {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;

        align-self: flex-start;
        width: 50% !important;
        margin: 0 !important;
        margin-top: 1em !important;
            
        > input {
            width: 100%;
            border: 0;
            outline: thin solid lightgray;
            border-radius: 100px;
            padding: .5em;

            &:focus {
                outline: thin solid gray !important;
            }
        }

        position: relative;
        > .search-btn {
            z-index: 99;
            transform-origin: (50%, 50%);
            position: absolute;
            right: 4.5em;
            top: 50%;
            transform: translateY(-60%);
            background: none;
            border: 0;
            outline: 0;
            scale: .8;
            transition: all 150ms;
            &:hover {
                scale: 1;
            }

            &:focus {
                outline: thin solid lightgray !important;

                > i {
                    color: black !important;
                }
            }
        }

        > .reset-btn {
            position: relative;
            background-color: var(--icon-yellow);
            color: whitesmoke;
            padding: .3em 1em;
            margin-left: .5em;
            border: 0;
            outline: thin solid gray;
            border-radius: .5em;
            
            transition: all 150ms;
            &:hover {
                filter: brightness(90%);
            }

            &:focus {
                outline: thin solid black !important;
            }

            &:active {
                outline: .125em solid black !important;
            }
        }
    }

    .companies-table {
        height: fit-content;
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: center;

        overflow: hidden;
        border-radius: 1em;
        filter: drop-shadow(0 .25em .5em gray);

        > .headers-row {
            width: 100%;
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-items: stretch;

            background-color: var(--sustainfinity-navy);
            color: whitesmoke;
            font-weight: bold;
            font-size: .75vw;

            > div {
                vertical-align: middle;
                padding: 1em .25em;

                border-right: thin solid whitesmoke;

                &:last-of-type {
                    border-right: 0;
                }

                &:nth-of-type(3), &:nth-of-type(7), &:nth-of-type(8) {
                    text-align: center !important;
                }

                cursor: pointer;
                &:hover {
                    text-decoration: underline;
                }
            }

            > .sorting-col {
                text-decoration: underline;
                display: flex;
                flex-direction: row;
                justify-content: center;
                align-items: center;
                
                > i {
                    height: fit-content !important;
                    padding-left: .25em;
                }
            }

            > .no-sorting {
                pointer-events: none !important;   
            }
        }

        > .values-row {
            width: 100%;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-items: center;

            
            border-bottom: thin solid lightgray;
            
            > .cells {
                min-height: 50px;
                width: 100%;
                display: flex;
                flex-direction: row;
                justify-content: flex-start;
                align-items: center;
                background-color: whitesmoke;
                color: var(--sustainfinity-navy);
                font-size: .75vw;

                transition: background-color 150ms;
                cursor: pointer;
                &:hover {
                    background-color: rgb(230, 230, 230);
                }

                > div {
                    word-break: break-word;
                    hyphens: auto;
                    -ms-hyphens: auto;
                    -moz-hyphens: auto;
                    -webkit-hyphens: auto;
                    padding: .5em .25em;
                    height: 100%;

                    &:nth-of-type(3), &:nth-of-type(7), &:nth-of-type(8) {
                        text-align: center !important;
                    }

                    position: relative;
                    > .no-users-icon {
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        color: var(--invalid-input);
                        > i {
                            transform: translate(-50%, -50%);
                        }
                    }
                }
            }

            > .stakeholders-ctr {
                width: 90%;
                height: fit-content;
            }
        }

        > .load-more-row {
            width: 100%;
            text-align: center;

            > span {
                > i {
                    transform: translateY(15%);
                }

                > .fa-spinner {
                    color: var(--sustainfinity-blue);
                    animation: Spin 500ms linear infinite;
                }
            }

            padding: .5em;
            background-color: rgb(230, 230, 230);
            transition: background-color 150ms;
            cursor: pointer;

            &:hover {
                background-color: lightgray;
            }

            &:focus {
                outline: thin solid black !important;
                color: black;
            }
        }
    }

    .edit-btn, .delete-btn {
        transition: filter 300ms;
        cursor: pointer;

        &:hover {
            filter: brightness(75%);
        }

        &:focus {
            outline: thin solid black;
        }
    }

    .edit-btn {
        color: var(--sustainfinity-blue);
    }

    .delete-btn {
        color: var(--invalid-input);
    }

    .edit-form {
        width: 100%;
        display: flex;
        flex-direction: row;
        justify-content: space-evenly;
        align-items: stretch;
        flex-wrap: wrap;

        > .small-field {
            width: 40%;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;

            > * {
                text-align: left;
                width: 100%;
            }

            > label {
                margin: 0dvh;
            }

            > input, textarea, select {
                padding: .35em .1em;
                border-radius: .25em;
                border: 0;
                outline: thin solid lightgray;

                &:focus {
                    outline: thin solid black !important;
                }
            }
        }
    }

    @keyframes Spin {
        0% {
            transform: rotate(0);
        } 100% {
            transform: rotate(360deg);
        }
    }
</style>